import { useState } from 'react';
import {
  usePatientNotes,
  useCreatePatientNote,
  useUpdatePatientNote,
  useDeletePatientNote,
} from '@/state/data/patient';

// Response id field is `id`; older shapes use `noteid`.
const noteKey = (note) => note.id ?? note.noteid;

function authorName(author) {
  if (!author) return 'Care team';
  const name = [author.firstname, author.lastname].filter(Boolean).join(' ');
  return name || 'Care team';
}

function formatNoteDate(epochSeconds) {
  if (!epochSeconds) return '';
  return new Date(epochSeconds * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export default function PatientNotes({ patient, currentUser }) {
  const userid = patient?.userid;
  // account.getuserslist returns the user's Withings id as `id` (program member
  // lists use `userid` for the same value). `userid` was always undefined here,
  // so createNote sent authorid="undefined" → scalews 503 Invalid Params.
  const myUserId = currentUser?.id ?? currentUser?.userid;

  const notesQuery = usePatientNotes(userid);
  const createNote = useCreatePatientNote();
  const updateNote = useUpdatePatientNote();
  const deleteNote = useDeletePatientNote();

  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Copy before sort — notesQuery.data.notes is the TanStack cache reference.
  const notes = [...(notesQuery.data?.notes || [])].sort(
    (a, b) => (b.modified ?? b.created ?? 0) - (a.modified ?? a.created ?? 0),
  );

  function handleAdd() {
    const message = draft.trim();
    if (!message || !userid) return;
    createNote.mutate(
      { userid, message, authorid: myUserId },
      { onSuccess: () => setDraft('') },
    );
  }

  function startEdit(note) {
    setConfirmDeleteId(null);
    setEditingId(noteKey(note));
    setEditText(note.message || '');
  }

  function cancelEdit() {
    setEditingId(null);
    setEditText('');
  }

  function handleSaveEdit(note) {
    const message = editText.trim();
    if (!message) return;
    updateNote.mutate(
      { noteid: noteKey(note), userid, message, authorid: myUserId },
      { onSuccess: cancelEdit },
    );
  }

  function handleDelete(note) {
    deleteNote.mutate(
      { noteid: noteKey(note), userid },
      { onSuccess: () => setConfirmDeleteId(null) },
    );
  }

  const ownsNote = (note) => myUserId != null && String(note.author?.userid) === String(myUserId);

  return (
    <div className="notes-section">
      <div className="section-title-component">
        <span className="header3">Notes</span>
        {notes.length > 0 && (
          <span className="detail1 text-secondary">{notes.length} note{notes.length !== 1 ? 's' : ''}</span>
        )}
      </div>

      <div className="note-composer">
        <label className="eyebrow" htmlFor="note-draft">Add a note</label>
        <textarea
          id="note-draft"
          className="note-textarea"
          rows={3}
          placeholder="Write a note about this patient…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="note-composer-actions">
          {createNote.error && <span className="detail1 note-error">{String(createNote.error.message)}</span>}
          <button
            type="button"
            className="btn-primary"
            onClick={handleAdd}
            disabled={!draft.trim() || createNote.isPending}
          >
            {createNote.isPending ? 'Saving…' : 'Add note'}
          </button>
        </div>
      </div>

      {notesQuery.isLoading ? (
        <div className="note-list">
          <div className="skeleton-line" style={{ width: '70%' }} />
          <div className="skeleton-line" style={{ width: '90%' }} />
          <div className="skeleton-line" style={{ width: '50%' }} />
        </div>
      ) : notesQuery.error ? (
        <p className="body2 note-error">Could not load notes: {String(notesQuery.error.message)}</p>
      ) : notes.length === 0 ? (
        <p className="body2 text-secondary">No notes yet for this patient.</p>
      ) : (
        <div className="note-list">
          {notes.map((note) => {
            const noteId = noteKey(note);
            const editing = String(editingId) === String(noteId);
            return (
              <div key={noteId} className="note-item">
                <div className="note-meta">
                  <span className="body2 note-author">{authorName(note.author)}</span>
                  <span className="detail1 text-secondary">{formatNoteDate(note.modified ?? note.created)}</span>
                </div>

                {editing ? (
                  <>
                    <textarea
                      className="note-textarea"
                      rows={3}
                      aria-label="Edit note"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                    />
                    <div className="note-composer-actions">
                      {updateNote.error && <span className="detail1 note-error">{String(updateNote.error.message)}</span>}
                      <button type="button" className="btn-secondary" onClick={cancelEdit} disabled={updateNote.isPending}>Cancel</button>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => handleSaveEdit(note)}
                        disabled={!editText.trim() || updateNote.isPending}
                      >
                        {updateNote.isPending ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="body2 note-message">{note.message}</p>
                    {ownsNote(note) && (
                      String(confirmDeleteId) === String(noteId) ? (
                        <div className="note-actions">
                          <span className="detail1 text-secondary">Delete this note?</span>
                          <button type="button" className="note-link" onClick={() => setConfirmDeleteId(null)} disabled={deleteNote.isPending}>Cancel</button>
                          <button type="button" className="note-link note-link-danger" onClick={() => handleDelete(note)} disabled={deleteNote.isPending}>
                            {deleteNote.isPending ? 'Deleting…' : 'Confirm delete'}
                          </button>
                        </div>
                      ) : (
                        <div className="note-actions">
                          <button type="button" className="note-link" onClick={() => startEdit(note)}>Edit</button>
                          <button type="button" className="note-link note-link-danger" onClick={() => setConfirmDeleteId(noteId)}>Delete</button>
                        </div>
                      )
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
